const API_URL = '';
export { API_URL };

// Helper for API calls
const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore di rete' }));
    throw new Error(error.detail || 'Errore API');
  }
  
  return response.json();
};

// Pratiche API
export const praticheApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/pratiche${query ? `?${query}` : ''}`);
  },
  
  getOne: (praticaId) => apiCall(`/pratiche/${praticaId}`),
  
  create: (data) => apiCall('/pratiche', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (praticaId, data) => apiCall(`/pratiche/${praticaId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (praticaId) => apiCall(`/pratiche/${praticaId}`, {
    method: 'DELETE',
  }),

  // Hierarchical assignment
  assignSede: (praticaId, sede_id) => apiCall(`/pratiche/${praticaId}/assign-sede`, {
    method: 'PUT',
    body: JSON.stringify({ sede_id }),
  }),
  assignOperatore: (praticaId, operatore_id) => apiCall(`/pratiche/${praticaId}/assign-operatore`, {
    method: 'PUT',
    body: JSON.stringify({ operatore_id }),
  }),
  revokeAssignment: (praticaId) => apiCall(`/pratiche/${praticaId}/revoke-assignment`, {
    method: 'PUT',
  }),
  
  // Nuovo: cliente invia/conferma la pratica
  submit: (praticaId) => apiCall(`/pratiche/${praticaId}/submit`, {
    method: 'POST',
  }),
  
  requestDocuments: (praticaId, data) => apiCall(`/pratiche/${praticaId}/request-documents`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  uploadDocument: async (praticaId, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    
    const response = await fetch(`${API_URL}/api/pratiche/${praticaId}/documents`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload fallito' }));
      throw new Error(error.detail || 'Upload fallito');
    }
    
    return response.json();
  },
};

// Cliente Profile API (anagrafica estesa + opportunità)
export const clienteProfileApi = {
  get: (userId) => apiCall(`/clienti/${userId}/profile`),
  update: (userId, data) => apiCall(`/clienti/${userId}/profile`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  opportunities: (userId) => apiCall(`/clienti/${userId}/opportunities`),
};

// Commissioni / Provvigioni API
export const commissionApi = {
  // % Sede (Super Admin only on write)
  getSedeRates: (sedeId) => apiCall(`/sedi/${sedeId}/commission-rates`),
  setSedeRates: (sedeId, rates) => apiCall(`/sedi/${sedeId}/commission-rates`, {
    method: 'PUT',
    body: JSON.stringify({ rates }),
  }),
  // % Operatore
  getOperatoreRates: (userId) => apiCall(`/operatori/${userId}/commission-rates`),
  setOperatoreRates: (userId, rates) => apiCall(`/operatori/${userId}/commission-rates`, {
    method: 'PUT',
    body: JSON.stringify({ rates }),
  }),
  // Commissione pratica (Super Admin only)
  updatePratica: (praticaId, payload) => apiCall(`/pratiche/${praticaId}/commission`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
  // Report finanziario
  financialReport: () => apiCall('/reports/financial'),
  // Alert pratiche evase con commissione 0
  zeroCommissionAlert: () => apiCall('/admin/zero-commission-alert'),
};

// Economic Terms (Architettura Provvigionale Ibrida)
export const economicTermsApi = {
  catalog: () => apiCall('/economic-terms/catalog'),
  getSede: (sedeId) => apiCall(`/sedi/${sedeId}/economic-terms`),
  setSede: (sedeId, items) => apiCall(`/sedi/${sedeId}/economic-terms`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  }),
  getOperatore: (userId) => apiCall(`/operatori/${userId}/economic-terms`),
  setOperatore: (userId, items) => apiCall(`/operatori/${userId}/economic-terms`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  }),
};

// Documents API
export const documentsApi = {
  getForPratica: (praticaId) => apiCall(`/pratiche/${praticaId}/documents`),
  
  upload: async (praticaId, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    
    const response = await fetch(`${API_URL}/api/pratiche/${praticaId}/documents`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload fallito' }));
      throw new Error(error.detail || 'Upload fallito');
    }
    
    return response.json();
  },
  
  download: async (docId, filename) => {
    // Auth via httpOnly cookie (credentials: include).
    // Niente lettura token da localStorage: lì non viene MAI scritto il token (vulnerabile a XSS).
    const response = await fetch(`${API_URL}/api/documents/${docId}/download`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Download fallito');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'documento';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
  
  delete: (docId) => apiCall(`/documents/${docId}`, {
    method: 'DELETE',
  }),
};

// Document Types API
export const documentTypesApi = {
  getForService: (serviceType, subType = null) => {
    const url = subType 
      ? `/document-types/${serviceType}?sub_type=${subType}`
      : `/document-types/${serviceType}`;
    return apiCall(url);
  },
  getAll: () => apiCall('/document-types'),
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false) => apiCall(`/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  
  markRead: (notificationId) => apiCall(`/notifications/${notificationId}/read`, {
    method: 'PUT',
  }),
  
  markAllRead: () => apiCall('/notifications/read-all', {
    method: 'PUT',
  }),
};

// Appointments API
export const appointmentsApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/appointments${query ? `?${query}` : ''}`);
  },
  
  create: (data) => apiCall('/appointments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (appointmentId, data) => apiCall(`/appointments/${appointmentId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  getAvailability: (sedeId, date) => apiCall(`/sedi/${sedeId}/availability?date=${date}`),
  
  getCalendar: (sedeId, month, year) => apiCall(`/appointments/calendar/${sedeId}?month=${month}&year=${year}`),
};

// Consultants API
export const consultantsApi = {
  getForSede: (sedeId) => apiCall(`/sedi/${sedeId}/consultants`),
  
  create: (sedeId, data) => apiCall(`/sedi/${sedeId}/consultants`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateAvailability: (consultantId, availability) => apiCall(`/consultants/${consultantId}/availability`, {
    method: 'PUT',
    body: JSON.stringify(availability),
  }),
};

// Stats API
export const statsApi = {
  get: () => apiCall('/stats'),
  getGlobal: () => apiCall('/stats/global'),
  getForSede: (sedeId) => apiCall(`/sedi/${sedeId}/stats`),
  getSedeDetailed: (sedeId) => apiCall(`/sedi/${sedeId}/stats/detailed`),
};

// Sedi API
export const sediApi = {
  getAll: () => apiCall('/sedi'),
  create: (data) => apiCall('/sedi', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (sedeId, data) => apiCall(`/sedi/${sedeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (sedeId) => apiCall(`/sedi/${sedeId}`, {
    method: 'DELETE',
  }),
  assignAdmin: (sedeId, userId) => apiCall(`/sedi/${sedeId}/assign-admin`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  }),
  getResponsabili: () => apiCall('/sede/responsabili'),
  getPublicResponsabili: () => apiCall('/public/responsabili'),
  getPublicLocations: () => apiCall('/public/locations'),
  // Ottiene tutte le sedi attive (per dropdown cambio ufficio)
  getPublicSediByCAP: (cap = '') => apiCall(`/public/sedi-by-cap${cap ? `?cap=${cap}` : ''}`),
  // Sedi disponibili per la registrazione (pubblico)
  getSediByCAP: async (cap) => {
    const response = await fetch(`${API_URL}/api/public/sedi-by-cap${cap ? `?cap=${cap}` : ''}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Errore nel caricamento delle sedi');
    }
    return response.json();
  },
};

// Admin utilities API
export const adminApi = {
  // Riassegna pratiche e utenti orfani alla sede principale
  cleanupOrphanPratiche: () => apiCall('/admin/cleanup-orphan-pratiche', {
    method: 'POST',
  }),
};

// Operatori API
export const operatoriApi = {
  getAll: () => apiCall('/operatori'),
  
  getForSede: (sedeId) => apiCall(`/sedi/${sedeId}/operatori`),
  
  create: (sedeId, data) => apiCall(`/sedi/${sedeId}/operatori`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (userId, data) => apiCall(`/operatori/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (userId) => apiCall(`/operatori/${userId}`, {
    method: 'DELETE',
  }),
};

// Pratica Assignment
export const praticaAssignApi = {
  assign: (praticaId, operatoreId) => apiCall(`/pratiche/${praticaId}/assign`, {
    method: 'PUT',
    body: JSON.stringify({ operatore_id: operatoreId }),
  }),
};

// Users API
export const usersApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/users${query ? `?${query}` : ''}`);
  },
  updateRole: (userId, role, sedeId = null) => apiCall(`/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role, sede_id: sedeId }),
  }),
};

// WhatsApp Contact API
export const whatsappApi = {
  getContact: () => apiCall('/whatsapp/contact'),
};

// Pratica View API (for auto status change)
export const praticaViewApi = {
  view: (praticaId) => apiCall(`/pratiche/${praticaId}/view`, { method: 'POST' }),
};

// Partner Leads API
export const partnerApi = {
  createLead: (data) => {
    // Public endpoint - no auth
    return fetch(`${API_URL}/api/partner/lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(res => {
      if (!res.ok) return res.json().then(e => { throw new Error(e.detail || 'Errore'); });
      return res.json();
    });
  },
  getAll: () => apiCall('/partner/leads'),
  update: (leadId, data) => apiCall(`/partner/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Marketing Files API
export const marketingApi = {
  getAll: () => apiCall('/marketing/files'),
  upload: async (file, description) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    
    const response = await fetch(`${API_URL}/api/marketing/files`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload fallito' }));
      throw new Error(error.detail || 'Upload fallito');
    }
    
    return response.json();
  },
  download: (fileId) => `${API_URL}/api/marketing/files/${fileId}/download`,
  delete: (fileId) => apiCall(`/marketing/files/${fileId}`, { method: 'DELETE' }),
};

// Aggiornamenti (Document Repository) API
export const aggiornamentiApi = {
  getAll: (category = null) => {
    const query = category ? `?category=${category}` : '';
    return apiCall(`/aggiornamenti/files${query}`);
  },
  getCategories: () => apiCall('/aggiornamenti/categories'),
  upload: async (file, category, title, description, expiry_date) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('title', title);
    if (description) formData.append('description', description);
    if (expiry_date) formData.append('expiry_date', expiry_date);
    
    const response = await fetch(`${API_URL}/api/aggiornamenti/files`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload fallito' }));
      throw new Error(error.detail || 'Upload fallito');
    }
    
    return response.json();
  },
  download: (fileId) => `${API_URL}/api/aggiornamenti/files/${fileId}/download`,
  delete: (fileId) => apiCall(`/aggiornamenti/files/${fileId}`, { method: 'DELETE' }),
};

// Obiettivi API
export const obiettiviApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.sede_id) query.append('sede_id', params.sede_id);
    if (params.mese) query.append('mese', params.mese);
    if (params.anno) query.append('anno', params.anno);
    const queryStr = query.toString();
    return apiCall(`/obiettivi${queryStr ? '?' + queryStr : ''}`);
  },
  create: (data) => apiCall('/obiettivi', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (obiettivoId, data) => apiCall(`/obiettivi/${obiettivoId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (obiettivoId) => apiCall(`/obiettivi/${obiettivoId}`, {
    method: 'DELETE',
  }),
};

// Performance API
export const performanceApi = {
  getSede: (sedeId, mese, anno) => {
    const params = new URLSearchParams();
    if (mese) params.append('mese', mese);
    if (anno) params.append('anno', anno);
    const queryStr = params.toString();
    return apiCall(`/performance/sede/${sedeId}${queryStr ? '?' + queryStr : ''}`);
  },
  getOperatore: (userId, mese, anno) => {
    const params = new URLSearchParams();
    if (mese) params.append('mese', mese);
    if (anno) params.append('anno', anno);
    const queryStr = params.toString();
    return apiCall(`/performance/operatore/${userId}${queryStr ? '?' + queryStr : ''}`);
  },
};

// Agenti API (for Admin Sede dashboard)
export const agentiApi = {
  getStats: () => apiCall('/agenti/stats'),
};

// Aggiornamenti categories mapping
export const UPDATE_CATEGORIES = {
  circolari: { label: 'Circolari', icon: 'FileText', color: 'blue' },
  listini: { label: 'Listini', icon: 'List', color: 'green' },
  convenzioni: { label: 'Convenzioni', icon: 'Handshake', color: 'purple' },
};

// Service types mapping
export const SERVICE_TYPES = {
  luce_gas: { label: 'Luce e Gas', icon: 'Zap', color: 'yellow' },
  fotovoltaico: { label: 'Fotovoltaico', icon: 'Sun', color: 'emerald' },
  caf: { label: 'Servizi CAF', icon: 'FileText', color: 'blue' },
  finanziamenti: { label: 'Finanziamenti', icon: 'Banknote', color: 'green' },
  assicurazioni: { label: 'Assicurazioni', icon: 'Shield', color: 'purple' },
  property_finder: { label: 'Property Finder', icon: 'Home', color: 'emerald' },
};

// Status mapping
export const STATUS_TYPES = {
  in_attesa: { label: 'In Attesa', color: 'warning' },
  in_lavorazione: { label: 'In Lavorazione', color: 'info' },
  in_stallo: { label: 'In Stallo', color: 'warning' },
  attesa_documenti: { label: 'Attesa Documenti', color: 'orange' },
  attesa_mandato: { label: 'Attesa Mandato', color: 'amber' },
  erogato: { label: 'Erogato', color: 'cyan' },
  venduto: { label: 'Venduto', color: 'sky' },
  evasa: { label: 'Evasa', color: 'success' },
  rifiutata: { label: 'Rifiutata', color: 'destructive' },
};

// Wallet API (estratto conto + saldo netto)
export const walletApi = {
  statement: (sedeId, year, month) => {
    const qs = (year && month) ? `?year=${year}&month=${month}` : '';
    return apiCall(`/wallet/sede/${sedeId}/statement${qs}`);
  },
  months: (sedeId) => apiCall(`/wallet/sede/${sedeId}/months`),
};

// Appointment status mapping
export const APPOINTMENT_STATUS = {
  scheduled: { label: 'Prenotato', color: 'blue' },
  completed: { label: 'Completato', color: 'green' },
  cancelled: { label: 'Cancellato', color: 'red' },
  no_show: { label: 'Non Presentato', color: 'orange' },
};

// Calendar API
export const calendarApi = {
  // Get events with filters
  getEvents: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.sede_id) queryParams.append('sede_id', params.sede_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.event_type) queryParams.append('event_type', params.event_type);
    return apiCall(`/calendar/events?${queryParams.toString()}`);
  },
  
  // Get week appointments (7 working days)
  getWeekAppointments: (sedeId) => {
    const params = sedeId ? `?sede_id=${sedeId}` : '';
    return apiCall(`/calendar/week${params}`);
  },
  
  // Create event
  createEvent: (data) => apiCall('/calendar/events', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Delete event
  deleteEvent: (eventId) => apiCall(`/calendar/events/${eventId}`, {
    method: 'DELETE',
  }),
  
  // Export to Google Calendar
  exportToGoogle: (eventId) => apiCall(`/calendar/export/${eventId}`),
};

// Scadenze API
export const scadenzeApi = {
  // Get all scadenze
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.pratica_id) queryParams.append('pratica_id', params.pratica_id);
    if (params.sede_id) queryParams.append('sede_id', params.sede_id);
    if (params.include_expired !== undefined) queryParams.append('include_expired', params.include_expired);
    return apiCall(`/scadenze?${queryParams.toString()}`);
  },
  
  // Get scadenze for a pratica (scadenziario)
  getForPratica: (praticaId) => apiCall(`/scadenze/pratica/${praticaId}`),
  
  // Create scadenza
  create: (data) => apiCall('/scadenze', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Delete scadenza
  delete: (scadenzaId) => apiCall(`/scadenze/${scadenzaId}`, {
    method: 'DELETE',
  }),
};

// Calendar event types and colors
export const CALENDAR_EVENT_TYPES = {
  annotazione: { label: 'Annotazione', color: 'bg-blue-500', textColor: 'text-blue-400', borderColor: 'border-blue-500' },
  cross_selling: { label: 'Cross Selling', color: 'bg-green-500', textColor: 'text-green-400', borderColor: 'border-green-500' },
  evento_importante: { label: 'Evento Importante', color: 'bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500', textColor: 'text-purple-400', borderColor: 'border-purple-500' },
  scadenza: { label: 'Scadenza', color: 'bg-orange-500', textColor: 'text-orange-400', borderColor: 'border-orange-500' },
  scadenza_passata: { label: 'Scaduto', color: 'bg-red-600', textColor: 'text-red-400', borderColor: 'border-red-600' },
  appuntamento: { label: 'Appuntamento', color: 'bg-cyan-500', textColor: 'text-cyan-400', borderColor: 'border-cyan-500' },
};

// Cashback API (Super Admin)
export const cashbackApi = {
  // Get all cashback configurations
  getConfig: () => apiCall('/cashback/config'),
  
  // Create or update cashback config
  saveConfig: (data) => apiCall('/cashback/config', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Delete cashback config
  deleteConfig: (configId) => apiCall(`/cashback/config/${configId}`, {
    method: 'DELETE',
  }),
};

// User Profile API
export const userProfileApi = {
  // Get user profile with stats
  getProfile: () => apiCall('/user/profile'),
  
  // Update profile (change sede)
  updateProfile: (data) => apiCall('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Get referral link and stats
  getReferralLink: () => apiCall('/user/referral-link'),
  
  // Get cashback history
  getCashbackHistory: () => apiCall('/user/cashback-history'),
};

// Properties (Immobiliare) API
export const propertiesApi = {
  list: (params = {}) => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const q = new URLSearchParams(clean).toString();
    return apiCall(`/properties${q ? `?${q}` : ''}`);
  },
  get: (id) => apiCall(`/properties/${id}`),
  counters: (only_mine = true) => apiCall(`/properties/counters?only_mine=${only_mine}`),
  create: async (formData) => {
    const response = await fetch(`${API_URL}/api/properties`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore upload' }));
      throw new Error(err.detail || 'Errore upload');
    }
    return response.json();
  },
  update: (id, data) => apiCall(`/properties/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  remove: (id) => apiCall(`/properties/${id}`, { method: 'DELETE' }),
  addPhotos: async (id, files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('photos', f));
    const response = await fetch(`${API_URL}/api/properties/${id}/photos`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore upload' }));
      throw new Error(err.detail || 'Errore upload');
    }
    return response.json();
  },
  deletePhoto: (id, photoId) => apiCall(`/properties/${id}/photos/${photoId}`, { method: 'DELETE' }),
  setCover: (id, photoId) => apiCall(`/properties/${id}/cover/${photoId}`, { method: 'PUT' }),
  photoUrl: (photoId) => `${API_URL}/api/properties/photo/${photoId}`,
  brochureUrl: (id) => `${API_URL}/api/properties/${id}/brochure`,
  downloadBrochure: async (id, filename = 'brochure.pdf') => {
    const response = await fetch(`${API_URL}/api/properties/${id}/brochure`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore download' }));
      throw new Error(err.detail || 'Errore download brochure');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// Property Finder Leads API (Step 3)
export const propertyFinderApi = {
  stats: () => apiCall('/property-finder/stats'),
  leads: (limit = 50) => apiCall(`/property-finder/leads?limit=${limit}`),
};

// Comparatore Bollette - CTE & Preventivi
export const cteApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return apiCall(`/cte${q ? `?${q}` : ''}`);
  },
  get: (id) => apiCall(`/cte/${id}`),
  create: async (formData) => {
    const response = await fetch(`${API_URL}/api/cte`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore creazione CTE' }));
      throw new Error(err.detail || 'Errore creazione CTE');
    }
    return response.json();
  },
  update: (id, data) => apiCall(`/cte/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  remove: (id) => apiCall(`/cte/${id}`, { method: 'DELETE' }),
  pdfUrl: (id) => `${API_URL}/api/cte/${id}/pdf`,
};

export const billQuoteApi = {
  parse: async (file, tipoHint) => {
    const fd = new FormData();
    fd.append('pdf', file);
    if (tipoHint) fd.append('tipo_hint', tipoHint);
    const response = await fetch(`${API_URL}/api/bill-quotes/parse`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore parser' }));
      throw new Error(err.detail || 'Errore parser');
    }
    return response.json();
  },
  compute: (data) => apiCall('/bill-quotes/compute', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  save: (data) => apiCall('/bill-quotes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  list: (limit = 100) => apiCall(`/bill-quotes?limit=${limit}`),
  get: (id) => apiCall(`/bill-quotes/${id}`),
  remove: (id) => apiCall(`/bill-quotes/${id}`, { method: 'DELETE' }),
  pdfUrl: (id) => `${API_URL}/api/bill-quotes/${id}/pdf`,
  downloadPdf: async (id, filename = 'preventivo.pdf', opts = {}) => {
    const qs = opts && opts.cte_id ? `?cte_id=${encodeURIComponent(opts.cte_id)}` : '';
    const response = await fetch(`${API_URL}/api/bill-quotes/${id}/pdf${qs}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Errore download' }));
      throw new Error(err.detail || 'Errore download');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  sendWhatsApp: async (id, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.cte_id) params.set('cte_id', opts.cte_id);
    if (opts.phone_override) params.set('phone_override', opts.phone_override);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/bill-quotes/${id}/send-whatsapp${qs}`, { method: 'POST' });
  },
};


// AI Usage Monitoring (Super Admin)
export const aiUsageApi = {
  stats: () => apiCall('/ai-usage/stats'),
  logs: (limit = 50, status) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set('status', status);
    return apiCall(`/ai-usage/logs?${params.toString()}`);
  },
};
